import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
export function cn(...inputs) {
    return twMerge(clsx(inputs));
}
const PLAIN_AMOUNT = /^([+-]?)(\d+)(?:\.(\d+))?$/;
const GROUPED_AMOUNT = /^([+-]?)(\d{1,3}(?:,\d{3})+)(?:\.(\d+))?$/;
const BASE_UNITS = /^([+-]?)(\d+)$/;
/**
 * Read an amount exactly, or return null when it can't be read. Accepts a decimal
 * string ("1234.5", "-3"), one with correct thousands grouping ("1,234.56"), a
 * bigint, or a number that still holds its digits: at most Number.MAX_SAFE_INTEGER
 * in size and printed without an exponent (so not 1e-7 or 1e21). Anything larger
 * or smaller must arrive as a string or a bigint. With `decimals`, `amount` is an
 * integer count of base units (USDC: 6), so "1500000" reads as 1.5; a number count
 * must be a safe integer. Missing, empty or anything else is null: the caller
 * shows the amount as unavailable, never as 0.
 */
export function parseAmountExact(amount, decimals) {
    let s;
    if (typeof amount === "string")
        s = amount.trim();
    else if (typeof amount === "number") {
        // Past 2^53 a number has already lost digits (JSON 9007199254740993 arrives as
        // ...992), so it can't be shown exactly.
        if (!Number.isFinite(amount) || Math.abs(amount) > Number.MAX_SAFE_INTEGER)
            return null;
        if (decimals !== undefined && !Number.isSafeInteger(amount))
            return null;
        s = String(amount);
    }
    else if (typeof amount === "bigint")
        s = amount.toString();
    else
        return null;
    if (decimals !== undefined) {
        if (!Number.isInteger(decimals) || decimals < 0 || decimals > 36)
            return null;
        const m = BASE_UNITS.exec(s);
        if (!m)
            return null;
        const digits = m[2].padStart(decimals + 1, "0");
        const cut = digits.length - decimals;
        return exactAmount(m[1] === "-", digits.slice(0, cut), digits.slice(cut));
    }
    const m = PLAIN_AMOUNT.exec(s) ?? GROUPED_AMOUNT.exec(s);
    if (!m)
        return null;
    return exactAmount(m[1] === "-", m[2].replace(/,/g, ""), m[3] ?? "");
}
function exactAmount(negative, whole, fraction) {
    const w = whole.replace(/^0+(?=\d)/, "");
    const zero = /^0+$/.test(w) && /^0*$/.test(fraction);
    return { negative: negative && !zero, whole: w, fraction };
}
const WHOLE_GROUPING = new Intl.NumberFormat("en-US", { useGrouping: true });
/**
 * Print an exact amount: the whole part grouped ("1,234"), then every significant
 * fraction digit, at least `minFractionDigits`. It never rounds, so 0.004 prints
 * as 0.004 and a real non-zero amount is never shown as 0.00.
 */
export function formatAmountExact(amount, minFractionDigits = 2) {
    const whole = WHOLE_GROUPING.format(BigInt(amount.whole));
    const fraction = amount.fraction.replace(/0+$/, "").padEnd(minFractionDigits, "0");
    return `${amount.negative ? "-" : ""}${whole}${fraction ? "." + fraction : ""}`;
}
export function formatAddress(address, chars = 4) {
    if (address.length <= chars * 2 + 2)
        return address;
    return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`;
}
export function formatHash(hash, chars = 6) {
    if (hash.length <= chars * 2)
        return hash;
    return `${hash.slice(0, chars)}...${hash.slice(-chars)}`;
}
export function formatDuration(seconds) {
    if (seconds < 60)
        return `${seconds}s`;
    if (seconds < 3600)
        return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
}
//# sourceMappingURL=utils.js.map